import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchLearningPlanDetails as fetchLearningPlanDetailsService } from '../services/learning-plan.service';

const useLearningPlanDetailsHook = (learningPlanKey) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [state, setState] = useState({
    learningPlan: null,
    pillars: [],
    loading: true,
    error: null
  });

  const fetchLearningPlanDetails = useCallback(async () => {
    if (!learningPlanKey) {
      navigate('/learning-plans');
      return;
    }

    // Get the plan object from navigation state
    const planFromState = location.state?.plan;
    
    if (!planFromState || !planFromState?.id) {
      navigate('/learning-plans');
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Pass the ID to the service
      const plan = await fetchLearningPlanDetailsService(planFromState.id);
      
      const learningPlan = {
        id: plan?.id,
        title: plan?.title,
        key: plan?.key,
        description: plan?.description
      };

      // Extract pillars array
      const pillars = plan?.pillars || [];

      setState({
        learningPlan,
        pillars,
        loading: false,
        error: null
      });
    } catch (err) {
      console.error('Error fetching learning plan details:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to fetch learning plan details'
      }));
    }
  }, [learningPlanKey, location.state]);

  useEffect(() => {
    fetchLearningPlanDetails();
  }, [fetchLearningPlanDetails]);

  const handleBackToList = () => {
    navigate('/learning-plans');
  };

  /**
   * Navigates to the configure rules page for a given pillar key
   * @param {string} pillarId - The pillar id to configure rules for
   */
  const handleConfigureRules = (pillarId) => {
    if (!state.learningPlan || !pillarId) return;

    const pillarData = state.pillars.find(pillar => pillar.id === pillarId);
    if (!pillarData) return;

    // Navigate to the configure rules page with state data
    const planKey = encodeURIComponent(state.learningPlan.key);
    const pillarName = encodeURIComponent(pillarData.title);
    navigate(`/learning-plans/${planKey}/pillars/${pillarName}/configure-rules`, {
      state: {
        planId: state.learningPlan.id,
        planKey: state.learningPlan.key,
        pillarData: pillarData
      }
    });
  };

  return {
    learningPlan: state.learningPlan,
    pillars: state.pillars,
    loading: state.loading,
    error: state.error,
    refreshLearningPlanDetails: fetchLearningPlanDetails,
    handleBackToList,
    handleConfigureRules
  };
};

export default useLearningPlanDetailsHook;


